/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  HeaderEnum,
  StorageEnum,
  UserAuthInterface,
  UserInterface,
  UserRolesEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { rolePermissions } from "types";

/**
 * Redux state for user authentication and authorization.
 * Manages user credentials, permissions, and authentication status.
 */
export type AuthState = {
  isAuthenticated?: boolean;
  userAuth?: UserAuthInterface | null;
  user?: UserInterface | null;
  message?: string | null;
  permissions?: string[];
};

const initialState: AuthState = {
  isAuthenticated: false,
  userAuth: null,
  user: null,
  message: null,
  permissions: [],
};

/**
 * Retrieves permission strings for a given user role from role-permission mapping.
 * @param role - User role enum value
 * @returns Array of permission strings for the role
 */
const getPermissionsForRole = (role: UserRolesEnum): string[] => rolePermissions[role] || [];

/**
 * Synchronizes user permissions based on their assigned roles.
 * Flattens and deduplicates permissions from all user roles.
 * @param state - Current authentication state
 */
const syncPermissions = (state: AuthState) => {
  if (state.user?.roles?.length) {
    state.permissions = state.user.roles
      .flatMap(getPermissionsForRole)
      .filter((perm, index, self) => self.indexOf(perm) === index);
  } else {
    state.permissions = [];
  }
};

/**
 * Updates authentication information in state without persisting to localStorage.
 * @param state - Current authentication state
 * @param action - Payload containing user authentication data
 */
const updateAuth = (state: AuthState, action: PayloadAction<UserAuthInterface>) => {
  state.userAuth = action.payload;
};

const updateUser = (state: AuthState, action: PayloadAction<UserInterface>) => {
  state.user = action.payload;
  syncPermissions(state);
};

/**
 * Stores authentication data in both Redux state and localStorage.
 * Sets authenticated flag and persists access token and user ID.
 * @param state - Current authentication state
 * @param action - Payload containing user authentication data
 */
const storeAuth = (state: AuthState, action: PayloadAction<UserAuthInterface>) => {
  state.userAuth = action.payload;
  state.isAuthenticated = true;
  window.localStorage.setItem(StorageEnum.ACCESS_TOKEN_ID, action.payload.accessToken);
  window.localStorage.setItem(StorageEnum.USER_AUTH, JSON.stringify(action.payload));
  window.localStorage.setItem(HeaderEnum.USER_ID, action.payload.id);
};

const storeUser = (state: AuthState, action: PayloadAction<UserInterface>) => {
  window.localStorage.setItem(StorageEnum.USER, JSON.stringify(action.payload));
  state.user = action.payload;
  syncPermissions(state);
};

// sync Stored User
const syncStoredUser = (state: AuthState) => {
  const userId = window.localStorage.getItem(HeaderEnum.USER_ID);
  const accessToken = window.localStorage.getItem(StorageEnum.ACCESS_TOKEN_ID);

  const authUser: UserAuthInterface = { id: userId, accessToken: accessToken };
  state.userAuth = authUser;
};

//TODO: create async thunk
const restoreUserAuthUser = (state: AuthState) => {
  const userAuthStr = window.localStorage.getItem(StorageEnum.USER_AUTH);
  if (userAuthStr) {
    state.userAuth = JSON.parse(userAuthStr);
    state.isAuthenticated = true;
  }

  const userStr = window.localStorage.getItem(StorageEnum.USER);
  if (userStr) {
    state.user = JSON.parse(userStr) as UserInterface;
    syncPermissions(state);
  } else {
    state.isAuthenticated = false;
  }
};

/**
 * Logs out user by clearing all authentication data from state and localStorage.
 * Resets state to initial values and clears all stored credentials.
 * @param state - Current authentication state
 */
const logout = (state: AuthState) => {
  state.isAuthenticated = false;
  state.message = null;
  state.user = null;
  state.userAuth = null;
  window.localStorage.removeItem(StorageEnum.USER_AUTH);
  window.localStorage.removeItem(StorageEnum.ACCESS_TOKEN_ID);
  window.localStorage.removeItem(HeaderEnum.USER_ID);
  window.localStorage.removeItem(StorageEnum.USER);
  Object.assign(state, initialState);
  window.localStorage.clear();
  state.permissions = [];
};

const ThemeSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    storeAuth: storeAuth,
    storeUser: storeUser,
    restoreUserAuthUser: restoreUserAuthUser,
    logout: logout,
    updateAuth: updateAuth,
    updateUser: updateUser,
    syncStoredUser: (state) => {
      syncStoredUser(state);
      syncPermissions(state);
    },
  },
});

export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.permissions || [];

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;

export const selectUserRoles = (state: { auth: AuthState }) => state.auth.user?.roles || [];

export const selectIsPatient = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.PATIENT)

export const selectIsDoctor = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.DOCTOR)

export const selectIsHeadNurse = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.HEAD_NURSE)

export const selectIsProvider = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.NURSE) || state.auth.user?.roles?.includes(UserRolesEnum.DOCTOR)

export const selectIsPatientOrDoctor = (state: { auth: AuthState }) => {
  const userRoles = selectUserRoles(state);
  const adminLikeRoles = [
    UserRolesEnum.ADMIN,
    UserRolesEnum.ROOT,
    UserRolesEnum.SECRETARY_RECEPTIONIST,
    UserRolesEnum.MAINTAINER,
    UserRolesEnum.HEAD_NURSE,
  ];
  const hasAdminLikeRole = userRoles.some((r) => adminLikeRoles.includes(r as UserRolesEnum));

  return (
    userRoles.includes(UserRolesEnum.PATIENT) ||
    (userRoles.includes(UserRolesEnum.DOCTOR) && !hasAdminLikeRole)
  );
};

export const selectHasAdminPermissions = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.ADMIN) || state.auth.user?.roles?.includes(UserRolesEnum.ROOT);

export const selectHasRootPermissions = (state: { auth: AuthState }) => state.auth.user?.roles?.includes(UserRolesEnum.ROOT);

export const selectHasPermission = (permission: string) => (state: { auth: AuthState }) => state.auth.permissions?.includes(permission);

export const selectHasAnyPermission = (permissions: string[]) => (state: { auth: AuthState }) => permissions.some(permission => state.auth.permissions?.includes(permission))

export const selectHasAllPermissions = (permissions: string[]) => (state: { auth: AuthState }) => permissions.every(permission => state.auth.permissions?.includes(permission))

export default ThemeSlice;
